import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import Button from 'CommonComponents/Button';

/**
 * DependentLinesPanel
 *
 * Slide-out panel that lists all expense lines linked to a specific source
 * (revenue line or non-financial driver). Each row is clickable and navigates
 * to the expense edit page in the same tab.
 *
 * The panel is opened by dispatching a custom DOM event `dependentlines:open`
 * with the following detail:
 *
 *   {
 *     dependentsUrl: string,   // URL to GET /revenues/{revenue}/quantity-dependents
 *     sourceLabel:   string,   // human-readable label for the source line
 *   }
 *
 * It is also opened from QuantityImpactPreviewDialog via the same event.
 */

/**
 * PanelContent — inner component.
 *
 * Props:
 *   dependentsUrl  – URL to fetch dependents
 *   sourceLabel    – label for the source being viewed
 *   onClose        – close callback
 */
function PanelContent({ dependentsUrl, sourceLabel, onClose }) {
    const [dependents, setDependents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        axios
            .get(dependentsUrl)
            .then(({ data }) => {
                setDependents(data.dependents ?? []);
            })
            .catch(() => {
                setError($t('Failed to load dependent lines. Please try again.'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dependentsUrl]);

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-justify-end">
            {/* Backdrop */}
            <div className="tw-absolute tw-inset-0 tw-bg-black/30" onClick={onClose} aria-hidden="true" />

            {/* Panel */}
            <div className="tw-relative tw-bg-white tw-w-full tw-max-w-md tw-h-full tw-flex tw-flex-col tw-shadow">
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b">
                    <div>
                        <h3 className="tw-text-2xl tw-font-semibold tw-text-blue">{$t('Dependent Expense Lines')}</h3>
                        {sourceLabel && (
                            <p className="tw-text-xs tw-text-charcoal-50 tw-mt-0.5">
                                {$t('Source')}: <span className="tw-font-medium">{sourceLabel}</span>
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="tw-p-2 tw-text-charcoal-75 hover:tw-text-charcoal tw-cursor-pointer tw-flex-shrink-0 tw-rounded hover:tw-bg-gray-100"
                        aria-label={$t('Close')}
                    >
                        <svg className="tw-w-4 tw-h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="tw-flex-1 tw-overflow-y-auto">
                    {loading && (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
                            <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading...')}</p>
                        </div>
                    )}

                    {error && (
                        <div className="tw-p-4">
                            <p className="tw-text-sm tw-text-red-600">{error}</p>
                        </div>
                    )}

                    {!loading && !error && dependents.length === 0 && (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
                            <p className="tw-text-sm tw-text-charcoal-50">{$t('No linked expense lines found.')}</p>
                        </div>
                    )}

                    {!loading && !error && dependents.length > 0 && (
                        <ul className="tw-divide-y tw-divide-gray-100">
                            {dependents.map((dep, i) => (
                                <li key={i}>
                                    {dep.edit_url ? (
                                        <a href={dep.edit_url} className="tw-block tw-px-4 tw-py-3 hover:tw-bg-blue-50 tw-transition-colors">
                                            <DependentRow dep={dep} />
                                        </a>
                                    ) : (
                                        <div className="tw-px-4 tw-py-3">
                                            <DependentRow dep={dep} />
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Footer */}
                <div className="tw-p-4 tw-border-t tw-flex tw-items-center tw-justify-between">
                    <p className="tw-text-xs tw-text-charcoal-50">{!loading && `${dependents.length} ${dependents.length === 1 ? $t('line') : $t('lines')}`}</p>
                    <Button variant="secondary" onClick={onClose}>
                        {$t('Close')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function DependentRow({ dep }) {
    return (
        <>
            <p className="tw-text-sm tw-font-medium tw-text-charcoal tw-truncate">{dep.name}</p>
            {dep.source_meta?.label && (
                <p className="tw-text-xs tw-text-charcoal-50 tw-mt-0.5 tw-truncate">
                    {$t('Linked to')}: {dep.source_meta.label}
                </p>
            )}
        </>
    );
}

/**
 * DependentLinesPanelRoot
 *
 * Listens for `dependentlines:open` events and renders the panel.
 */
const DependentLinesPanelRoot = () => {
    const [state, setState] = useState(null); // null = hidden

    const handleOpen = useCallback((e) => {
        const { dependentsUrl, sourceLabel } = e.detail || {};
        if (!dependentsUrl) return;
        setState({ dependentsUrl, sourceLabel });
    }, []);

    useEffect(() => {
        window.addEventListener('dependentlines:open', handleOpen);
        return () => window.removeEventListener('dependentlines:open', handleOpen);
    }, [handleOpen]);

    if (!state) return null;

    return <PanelContent dependentsUrl={state.dependentsUrl} sourceLabel={state.sourceLabel} onClose={() => setState(null)} />;
};

const rootEl = document.getElementById('DependentLinesPanelApp');
if (rootEl) {
    ReactDOM.render(
        <App>
            <DependentLinesPanelRoot />
        </App>,
        rootEl,
    );
}
